// src/shared/context/AppContext.tsx

import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { AuthState, AuthAction } from '../../modules/auth/types';
import { UsersState, UsersAction } from '../../modules/users/types'; // Corregido: UsersState y UsersAction
import { CompetitionsState, CompetitionsAction } from '../../modules/competitions/types';


// Estado global de la aplicación
interface AppState {
  auth: AuthState;
  users: UsersState; // Corregido: UsersState
  competitions: CompetitionsState; // <-- AGREGAR ESTA LÍNEA
}



// Acciones combinadas
type AppAction = AuthAction | UsersAction | CompetitionsAction; // <-- AGREGAR CompetitionsAction

// Estado inicial
const initialState: AppState = {
  auth: {
    isAuthenticated: false,
    user: null,
    token: null,
    isLoading: false,
    error: null,
  },
  users: {
    currentUser: null,
    profile: null,
    dashboard: null,
    activities: [],
    sessions: [],
    preferences: null,
    isLoading: false,
    error: null,
  },
  competitions: { // <-- AGREGAR ESTE OBJETO
    competitions: [],
    currentCompetition: null,
    userParticipations: [],
    myRegistrations: [], // ✅ AGREGADO
    isLoading: false,
    error: null,
    pagination: null,
  },
};


// Reducer principal que combina todos los reducers de módulos
const appReducer = (state: AppState, action: AppAction): AppState => {
  switch (action.type) {
    // Acciones de autenticación
    case 'AUTH_LOGIN_START':
      return {
        ...state,
        auth: {
          ...state.auth,
          isLoading: true,
          error: null,
        },
      };

    case 'AUTH_LOGIN_SUCCESS':
      return {
        ...state,
        auth: {
          ...state.auth,
          isAuthenticated: true,
          user: action.payload.user,
          token: action.payload.token,
          isLoading: false,
          error: null,
        },
      };

    case 'AUTH_LOGIN_ERROR':
      return {
        ...state,
        auth: {
          ...state.auth,
          isAuthenticated: false,
          user: null,
          token: null,
          isLoading: false,
          error: action.payload.error,
        },
      };

    case 'AUTH_LOGOUT':
      return {
        ...state,
        auth: {
          ...state.auth,
          isAuthenticated: false,
          user: null,
          token: null,
          isLoading: false,
          error: null,
        },
        // Limpiar también datos de usuarios al hacer logout
        users: {
          ...initialState.users,
        },
      };

    case 'AUTH_CLEAR_ERROR':
      return {
        ...state,
        auth: {
          ...state.auth,
          error: null,
        },
      };

    case 'AUTH_UPDATE_USER':
      return {
        ...state,
        auth: {
          ...state.auth,
          user: action.payload.user,
        },
      };

    // Acciones de usuarios (corregidas)
    case 'USERS_SET_LOADING':
      return {
        ...state,
        users: {
          ...state.users,
          isLoading: action.payload.isLoading,
        },
      };

    case 'USERS_SET_ERROR':
      return {
        ...state,
        users: {
          ...state.users,
          error: action.payload.error,
          isLoading: false,
        },
      };

    case 'USERS_SET_CURRENT_USER':
      return {
        ...state,
        users: {
          ...state.users,
          currentUser: action.payload.user,
        },
      };

    case 'USERS_SET_PROFILE':
      return {
        ...state,
        users: {
          ...state.users,
          profile: action.payload.profile,
        },
      };

    case 'USERS_SET_DASHBOARD':
      return {
        ...state,
        users: {
          ...state.users,
          dashboard: action.payload.dashboard,
        },
      };

    case 'USERS_SET_ACTIVITIES':
      return {
        ...state,
        users: {
          ...state.users,
          activities: action.payload.activities,
        },
      };

    case 'USERS_SET_SESSIONS':
      return {
        ...state,
        users: {
          ...state.users,
          sessions: action.payload.sessions,
        },
      };

    case 'USERS_SET_PREFERENCES':
      return {
        ...state,
        users: {
          ...state.users,
          preferences: action.payload.preferences,
        },
      };

    case 'USERS_UPDATE_USER':
      return {
        ...state,
        users: {
          ...state.users,
          currentUser: state.users.currentUser ? {
            ...state.users.currentUser,
            ...action.payload.updates
          } : null,
        },
        // También actualizar en auth si es el mismo usuario
        auth: state.auth.user && state.users.currentUser?.id === state.auth.user.id ? {
          ...state.auth,
          user: {
            ...state.auth.user,
            ...action.payload.updates
          }
        } : state.auth,
      };

    case 'USERS_CLEAR_DATA':
      return {
        ...state,
        users: {
          ...initialState.users,
        },
      };

      case 'COMPETITIONS_LOADING':
      return {
        ...state,
        competitions: {
          ...state.competitions,
          isLoading: action.payload.isLoading,
        },
      };

    case 'COMPETITIONS_LIST_SUCCESS':
      return {
        ...state,
        competitions: {
          ...state.competitions,
          competitions: action.payload.data.competitions,
          pagination: action.payload.data.pagination,
          isLoading: false,
          error: null,
        },
      };

    case 'COMPETITIONS_DETAILS_SUCCESS':
      return {
        ...state,
        competitions: {
          ...state.competitions,
          currentCompetition: action.payload.data,
          isLoading: false,
          error: null,
        },
      };

    case 'COMPETITIONS_STATUS_SUCCESS':
      return {
        ...state,
        competitions: {
          ...state.competitions,
          userParticipations: [
            ...state.competitions.userParticipations.filter(
              p => p.event_id !== action.payload.data.participant_status.event_id
            ),
            action.payload.data.participant_status
          ],
          isLoading: false,
          error: null,
        },
      };

    case 'COMPETITIONS_ERROR':
      return {
        ...state,
        competitions: {
          ...state.competitions,
          isLoading: false,
          error: action.payload.error,
        },
      };

    case 'COMPETITIONS_CLEAR_ERROR':
      return {
        ...state,
        competitions: {
          ...state.competitions,
          error: null,
        },
      };

    case 'COMPETITIONS_RESET':
      return {
        ...state,
        competitions: {
          competitions: [],
          currentCompetition: null,
          userParticipations: [],
          myRegistrations: [], // ✅ AGREGADO
          isLoading: false,
          error: null,
          pagination: null,
        },
      };


    default:
      return state;
  }
};

// Context
interface AppContextType {
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

// Provider
interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // Verificar sesión guardada al inicializar
  React.useEffect(() => {
    const checkSavedSession = () => {
      const savedToken = localStorage.getItem('ibloom_auth_token');
      const savedUser = localStorage.getItem('ibloom_user_data');

      if (savedToken && savedUser) {
        try {
          const user = JSON.parse(savedUser);
          
          // Verificar si el token no ha expirado (básico)
          const payload = JSON.parse(atob(savedToken.split('.')[1]));
          const now = Math.floor(Date.now() / 1000);
          
          if (payload.exp > now) {
            dispatch({
              type: 'AUTH_LOGIN_SUCCESS',
              payload: { user, token: savedToken }
            });
          } else {
            // Token expirado, limpiar
            localStorage.removeItem('ibloom_auth_token');
            localStorage.removeItem('ibloom_user_data');
            localStorage.removeItem('ibloom_refresh_token');
          }
        } catch (error) {
          console.error('Error al verificar sesión guardada:', error);
          localStorage.removeItem('ibloom_auth_token');
          localStorage.removeItem('ibloom_user_data');
          localStorage.removeItem('ibloom_refresh_token');
        }
      }
    };

    checkSavedSession();
  }, []);

  // Guardar sesión cuando cambie el estado de auth
  React.useEffect(() => {
    if (state.auth.isAuthenticated && state.auth.user && state.auth.token) {
      localStorage.setItem('ibloom_auth_token', state.auth.token);
      localStorage.setItem('ibloom_user_data', JSON.stringify(state.auth.user));
    }
  }, [state.auth.isAuthenticated, state.auth.user, state.auth.token]);

  return (
    <AppContext.Provider value={{ state, dispatch }}>
      {children}
    </AppContext.Provider>
  );
};

// Hook personalizado
export const useAppContext = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useAppContext debe ser usado dentro de AppProvider');
  }
  return context;
};

export default AppContext;